import { useEffect, useState, useCallback } from "react";
import { useAuth } from "@/contexts/AuthContext";
import axios from "axios";
import {
  Loader2, Plus, Pencil, Trash2, Search, Target, TrendingUp, TrendingDown,
  AlertTriangle, Bell, Globe, Star, BarChart3, Eye, Shield, MapPin,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";

const API = `${(process.env.REACT_APP_BACKEND_URL || window.location.origin)}/api`;

function ScoreBar({ label, score, max = 100, color }) {
  return (
    <div className="space-y-1">
      <div className="flex justify-between text-xs">
        <span className="text-[#64748b]">{label}</span>
        <span style={{ color }}>{score}/{max}</span>
      </div>
      <div className="h-2 bg-[#0B0F19] rounded-full overflow-hidden">
        <div className="h-full rounded-full" style={{ width: `${(score / max) * 100}%`, backgroundColor: color }} />
      </div>
    </div>
  );
}

function CompetitorForm({ clients, initial, onSave, onCancel }) {
  const [form, setForm] = useState(initial || {
    client_id: "", name: "", website: "", google_business_url: "", notes: "",
    visibility_score: 0, seo_score: 0, content_score: 0, gmb_score: 0,
    review_count: 0, review_avg: 0,
  });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name) { toast.error("Nom requis"); return; }
    setSaving(true);
    try { await onSave(form); } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
    finally { setSaving(false); }
  };

  return (
    <form onSubmit={submit} className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div><Label className="text-[#94A3B8] text-xs">Client</Label>
          <select value={form.client_id} onChange={e => set("client_id", e.target.value)}
            className="w-full mt-1 bg-[#0B0F19] border border-[#2A3441] text-white rounded-lg px-3 py-2 text-sm">
            <option value="">Selectionnez...</option>
            {clients.map(c => <option key={c.id} value={c.id}>{c.company_name}</option>)}
          </select>
        </div>
        <div><Label className="text-[#94A3B8] text-xs">Nom concurrent</Label>
          <Input value={form.name} onChange={e => set("name", e.target.value)} className="mt-1 bg-[#0B0F19] border-[#2A3441] text-white" />
        </div>
        <div><Label className="text-[#94A3B8] text-xs">Site web</Label>
          <Input value={form.website} onChange={e => set("website", e.target.value)} placeholder="https://..." className="mt-1 bg-[#0B0F19] border-[#2A3441] text-white" />
        </div>
        <div><Label className="text-[#94A3B8] text-xs">Google Business URL</Label>
          <Input value={form.google_business_url} onChange={e => set("google_business_url", e.target.value)} className="mt-1 bg-[#0B0F19] border-[#2A3441] text-white" />
        </div>
        <div><Label className="text-[#94A3B8] text-xs">Score Visibilite (0-100)</Label>
          <Input type="number" value={form.visibility_score} onChange={e => set("visibility_score", Number(e.target.value))} className="mt-1 bg-[#0B0F19] border-[#2A3441] text-white" />
        </div>
        <div><Label className="text-[#94A3B8] text-xs">Score SEO (0-100)</Label>
          <Input type="number" value={form.seo_score} onChange={e => set("seo_score", Number(e.target.value))} className="mt-1 bg-[#0B0F19] border-[#2A3441] text-white" />
        </div>
        <div><Label className="text-[#94A3B8] text-xs">Score Contenu (0-100)</Label>
          <Input type="number" value={form.content_score} onChange={e => set("content_score", Number(e.target.value))} className="mt-1 bg-[#0B0F19] border-[#2A3441] text-white" />
        </div>
        <div><Label className="text-[#94A3B8] text-xs">Score Google Business (0-100)</Label>
          <Input type="number" value={form.gmb_score} onChange={e => set("gmb_score", Number(e.target.value))} className="mt-1 bg-[#0B0F19] border-[#2A3441] text-white" />
        </div>
        <div><Label className="text-[#94A3B8] text-xs">Nombre d'avis</Label>
          <Input type="number" value={form.review_count} onChange={e => set("review_count", Number(e.target.value))} className="mt-1 bg-[#0B0F19] border-[#2A3441] text-white" />
        </div>
        <div><Label className="text-[#94A3B8] text-xs">Note moyenne</Label>
          <Input type="number" step="0.1" value={form.review_avg} onChange={e => set("review_avg", Number(e.target.value))} className="mt-1 bg-[#0B0F19] border-[#2A3441] text-white" />
        </div>
      </div>
      <div><Label className="text-[#94A3B8] text-xs">Notes</Label>
        <textarea value={form.notes} onChange={e => set("notes", e.target.value)} rows={2}
          className="w-full mt-1 bg-[#0B0F19] border border-[#2A3441] text-white rounded-lg px-3 py-2 text-sm resize-none" />
      </div>
      <div className="flex gap-2">
        <Button type="submit" disabled={saving} className="bg-[#0099CC] hover:bg-[#0088B5] text-white">
          {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Plus className="h-4 w-4 mr-2" />}Enregistrer
        </Button>
        <Button type="button" variant="outline" onClick={onCancel} className="border-[#2A3441] text-[#94A3B8]">Annuler</Button>
      </div>
    </form>
  );
}

export default function CompetitorsPage() {
  const { isAdmin, clientId } = useAuth();
  const [competitors, setCompetitors] = useState([]);
  const [clients, setClients] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState(null);
  const [selectedClient, setSelectedClient] = useState("");

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = selectedClient ? `?client_id=${selectedClient}` : "";
      const [cmpRes, clRes] = await Promise.all([
        axios.get(`${API}/competitors${params}`),
        isAdmin ? axios.get(`${API}/clients`) : Promise.resolve({ data: [] }),
      ]);
      setCompetitors(cmpRes.data);
      setClients(clRes.data);
      // Fetch alerts for first client
      const targetClient = selectedClient || clientId;
      if (targetClient) {
        const aRes = await axios.get(`${API}/competitors/alerts/${targetClient}`);
        setAlerts(aRes.data);
      }
    } catch { toast.error("Erreur chargement"); }
    finally { setLoading(false); }
  }, [isAdmin, selectedClient, clientId]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const create = async (data) => { await axios.post(`${API}/competitors`, data); toast.success("Concurrent ajoute"); setCreating(false); fetchData(); };
  const update = async (data) => { await axios.put(`${API}/competitors/${editing.id}`, data); toast.success("Mis a jour"); setEditing(null); fetchData(); };
  const remove = async (id) => { if (!window.confirm("Supprimer ?")) return; await axios.delete(`${API}/competitors/${id}`); toast.success("Supprime"); fetchData(); };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="competitors-page">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-white">{isAdmin ? "Surveillance Concurrents" : "Mes Concurrents"}</h1>
          <p className="text-[#94A3B8] mt-1">Suivi SEO et visibilite des concurrents</p>
        </div>
        <div className="flex gap-2">
          {isAdmin && clients.length > 0 && (
            <select value={selectedClient} onChange={e => setSelectedClient(e.target.value)}
              className="bg-[#151B2B] border border-[#2A3441] text-white rounded-lg px-3 py-2 text-sm">
              <option value="">Tous les clients</option>
              {clients.map(c => <option key={c.id} value={c.id}>{c.company_name}</option>)}
            </select>
          )}
          {isAdmin && (
            <Button onClick={() => setCreating(true)} className="bg-[#0099CC] hover:bg-[#0088B5] text-white" data-testid="add-competitor-btn">
              <Plus className="h-4 w-4 mr-2" />Ajouter concurrent
            </Button>
          )}
        </div>
      </div>

      {/* Alerts */}
      {alerts.length > 0 && (
        <div className="space-y-2">
          {alerts.slice(0, 5).map(a => (
            <div key={a.id} className={`rounded-xl p-4 border flex items-center gap-3 ${
              a.severity === "critical" ? "bg-red-500/5 border-red-500/20" :
              a.severity === "warning" ? "bg-[#F59E0B]/5 border-[#F59E0B]/20" :
              "bg-[#0099CC]/5 border-[#0099CC]/20"
            }`} data-testid={`alert-${a.id}`}>
              <Bell className={`h-4 w-4 ${a.severity === "critical" ? "text-red-400" : a.severity === "warning" ? "text-[#F59E0B]" : "text-[#0099CC]"}`} />
              <span className="text-sm text-[#94A3B8] flex-1">{a.message}</span>
              <span className="text-xs text-[#64748b]">{new Date(a.created_at).toLocaleDateString("fr-FR")}</span>
            </div>
          ))}
        </div>
      )}

      {/* Create/Edit */}
      {creating && (
        <div className="bg-[#151B2B] border border-[#2A3441] rounded-xl p-6">
          <h3 className="text-white font-semibold mb-4">Ajouter un concurrent</h3>
          <CompetitorForm clients={clients} onSave={create} onCancel={() => setCreating(false)} />
        </div>
      )}
      {editing && (
        <div className="bg-[#151B2B] border border-[#2A3441] rounded-xl p-6">
          <h3 className="text-white font-semibold mb-4">Modifier le concurrent</h3>
          <CompetitorForm clients={clients} initial={editing} onSave={update} onCancel={() => setEditing(null)} />
        </div>
      )}

      {/* Competitors List */}
      {loading ? (
        <div className="flex justify-center py-16"><Loader2 className="h-8 w-8 animate-spin text-[#0099CC]" /></div>
      ) : competitors.length === 0 ? (
        <div className="text-center py-16 bg-[#151B2B] rounded-xl border border-[#2A3441]">
          <Shield className="h-12 w-12 text-[#64748b] mx-auto mb-4" />
          <h3 className="text-xl font-semibold text-white mb-2">Aucun concurrent</h3>
          <p className="text-[#94A3B8]">{isAdmin ? "Ajoutez des concurrents a surveiller." : "Vos concurrents surveilles apparaitront ici."}</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {competitors.map(c => (
            <div key={c.id} className="bg-[#151B2B] border border-[#2A3441] rounded-xl p-6 hover:border-[#0099CC]/30 transition-colors" data-testid={`competitor-card-${c.id}`}>
              <div className="flex items-start justify-between mb-4">
                <div>
                  <h3 className="text-white font-semibold">{c.name}</h3>
                  {c.website && (
                    <a href={c.website} target="_blank" rel="noopener noreferrer" className="text-[#0099CC] text-sm hover:underline flex items-center gap-1">
                      <Globe className="h-3 w-3" />{c.website}
                    </a>
                  )}
                </div>
                {isAdmin && (
                  <div className="flex gap-1">
                    <Button size="sm" variant="ghost" onClick={() => { setCreating(false); setEditing(c); }} className="text-[#94A3B8] h-8 w-8 p-0">
                      <Pencil className="h-3.5 w-3.5" />
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => remove(c.id)} className="text-[#EF4444] h-8 w-8 p-0">
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                )}
              </div>

              {/* Scores */}
              <div className="space-y-3 mb-4">
                <ScoreBar label="Visibilite" score={c.visibility_score || 0} color="#0099CC" />
                <ScoreBar label="SEO" score={c.seo_score || 0} color="#10B981" />
                <ScoreBar label="Contenu" score={c.content_score || 0} color="#F59E0B" />
                <ScoreBar label="Google Business" score={c.gmb_score || 0} color="#8B5CF6" />
              </div>

              {/* Reviews */}
              <div className="flex items-center gap-4 pt-3 border-t border-[#2A3441]">
                <div className="flex items-center gap-1">
                  <Star className="h-4 w-4 text-[#F59E0B]" />
                  <span className="text-white font-bold">{c.review_avg || 0}</span>
                </div>
                <span className="text-[#64748b] text-sm">{c.review_count || 0} avis</span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
